import ReactSelect, {GroupBase, mergeStyles, Props as SelectProps, StylesConfig, components} from 'react-select';
import {getVariantStyles, selectStyles, selectTheme} from '@/styles/selectTheme';
import React, {useRef} from 'react';
import {faXmark} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

interface CustomSelectProps<
    Option = unknown,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
> extends Omit<SelectProps<Option, IsMulti, Group>, 'theme' | 'styles'> {
    variant?: 'default' | 'modal' | 'inline';
    styles?: StylesConfig<Option, IsMulti, Group>;
}

export default function Select<
    Option = unknown,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>({
    variant = 'default',
    styles: instanceStyles = {},
    ...props
}: CustomSelectProps<Option, IsMulti, Group>) {
    const variantStyles = getVariantStyles(variant);
    const selectRef = useRef<any>(null);

    const finalStyles = mergeStyles(
        selectStyles,
        mergeStyles(
            variantStyles,
            instanceStyles
        )
    );
    const isClearable = props.isClearable !== undefined ? props.isClearable : false;
    const originalMenuList = finalStyles.menuList;
    finalStyles.menuList = (base, state) => {

        const mergedBase = originalMenuList ? originalMenuList(base, state) : base;

        const optionCount = props.options?.length || 0;
        const padding = optionCount <= 1 ? '0px' : '8px';

        return {
            ...mergedBase,
            padding: padding,
        };
    };

    const handleMenuOpen = () => {

        if (props.onMenuOpen) {
            props.onMenuOpen();
        }

        setTimeout(() => {
            if (selectRef.current && props.value) {
                const selectInstance = selectRef.current;

                const selectedOption = Array.isArray(props.options)
                    ? props.options.find(option => {
                        if (Array.isArray(props.value)) {
                            return props.value.some(val =>
                                (typeof val === 'object' && val !== null && 'value' in val)
                                    ? val.value === (option as any).value
                                    : val === option
                            );
                        } else {
                            return (typeof props.value === 'object' && props.value !== null && 'value' in props.value)
                                ? props.value.value === (option as any).value
                                : props.value === option;
                        }
                    })
                    : null;

                if (selectedOption) {

                    selectInstance.setState({
                        focusedOption: selectedOption,
                    }, () => {

                        selectInstance.scrollToFocusedOptionOnUpdate = true;
                        selectInstance.forceUpdate();
                    });
                }
            }
        }, 0);
    };

    return (
        <ReactSelect<Option, IsMulti, Group>
            ref={selectRef}
            isClearable={isClearable}
            theme={selectTheme}
            menuShouldScrollIntoView={true}
            onMenuOpen={handleMenuOpen}


            components={{
                ClearIndicator: (props) => (
                    <components.ClearIndicator {...props}>
                        <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text"/>
                    </components.ClearIndicator>
                ),
                ...(props.components || {})
            }}
            ariaLiveMessages={{
                guidance: () => '',
                onChange: () => '',
                onFilter: () => '',
                onFocus: () => ''
            }}
            styles={finalStyles}
            {...props}
        />
    );
}
